import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { assertPresence } from '../../../../lib/runtime';
import {
	CelebrationResponse,
	MonthlyCelebration,
	MonthlyCelebrationResult,
	MonthlyCelebrationsData,
	MonthlyCelebrationsResponse,
	UpcomingCelebration,
} from '../../types';

export const useCelebrationsQuery = () => {
	const http = useHttpClient('v2');

	const { data, isLoading, error } = useQuery(['celebrations-widget'], () => http.get<CelebrationResponse>('celebrations_widget'), {
		select: ({ data }) => data.result,
	});

	const sortedData = {
		recent: data?.recent.sort(sortByDate) || [],
		today: data?.today.sort(sortByDate) || [],
		upcoming: data?.upcoming.sort(sortUpcoming) || [],
	};

	return {
		data: sortedData || { recent: [], today: [], upcoming: [] },
		error,
		isLoading,
	};
};

// NOTE: upcoming celebrations have date format "YYYY-MM-DD"
// Birthdays always have year 1904
// Anniversaries have the year the user joined
// We need to set the year of the celebration to the current year so we can sort
// Unless it is December and the celebration is in January then it is next year
const sortUpcoming = (a: UpcomingCelebration, b: UpcomingCelebration) => {
	const aDate = new Date(a.date);
	aDate.setFullYear(new Date().getFullYear());
	const bDate = new Date(b.date);
	bDate.setFullYear(new Date().getFullYear());

	// NOTE: if the current month is December and the upcoming celebration is in January
	// then the year should be next year
	const currentMonth = new Date().getMonth();
	if (currentMonth === 11) {
		if (aDate.getMonth() === 0) {
			aDate.setFullYear(aDate.getFullYear() + 1);
		}
		if (bDate.getMonth() === 0) {
			bDate.setFullYear(bDate.getFullYear() + 1);
		}
	}

	return aDate.getTime() - bDate.getTime();
};

export const sortByDate = (a: MonthlyCelebration, b: MonthlyCelebration) => {
	// NOTE: only birthday and anniversary celebrations have the date field
	// so use created_at for welcome celebrations
	// also need to use slashes for dates for when using new Date() so it returns the correct date
	const aDate = a.date || a.created_at;

	assertPresence(aDate);

	const aSlashDate = new Date(aDate.replace(/-/g, '/').replace(/T.+/, ''));
	const bDate = b.date || b.created_at;

	assertPresence(bDate);

	const bSlashDate = new Date(bDate.replace(/-/g, '/').replace(/T.+/, ''));

	// only need to use day, the month should all be the same and year does not matter
	return aSlashDate.getDate() - bSlashDate.getDate();
};

export const useMonthlyCelebrationsQuery = () => {
	const http = useHttpClient('v2');
	const emptyResult: MonthlyCelebrationResult = {
		all: { recent: [], upcoming: [] },
		relevant: { recent: [], upcoming: [] },
	};
	const emptyData: MonthlyCelebrationsData = {
		all: {
			count: 0,
			recent: [],
			upcoming: [],
		},
		relevant: {
			count: 0,
			recent: [],
			upcoming: [],
		},
	};

	const { data, isLoading, error } = useQuery(['celebrations-modal'], () => http.get<MonthlyCelebrationsResponse>('celebrations_modal'), {
		select: ({ data }) => {
			const result = data.result ? data.result : emptyResult;

			const allRecentSet = new Set(result.all.recent.map((item) => `${item.user_id}-${item.type}`));
			const allUpcomingFilterDuplicates = result.all.upcoming.filter((item) => !allRecentSet.has(`${item.user_id}-${item.type}`));

			const all = {
				recent: [...result.all.recent].sort(sortByDate),
				upcoming: [...allUpcomingFilterDuplicates].sort(sortByDate),
			};
			const allCount = all.recent.length + all.upcoming.length;

			const relevantRecentSet = new Set(result.relevant.recent.map((item) => `${item.user_id}-${item.type}`));
			const relevantUpcomingFilterDuplicates = result.relevant.upcoming.filter((item) => !relevantRecentSet.has(`${item.user_id}-${item.type}`));

			const relevant = {
				recent: [...result.relevant.recent].sort(sortByDate),
				upcoming: [...relevantUpcomingFilterDuplicates].sort(sortByDate),
			};
			const relevantCount = relevant.recent.length + relevant.upcoming.length;

			return {
				all: { ...all, count: allCount },
				relevant: { ...relevant, count: relevantCount },
			};
		},
	});

	return {
		data: data || emptyData,
		error,
		isLoading,
	};
};
